import React, { useState, useEffect } from "react";
import { Download, Smartphone, X } from "lucide-react";

export default function InstallPwaPrompt({ className = "" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed in standalone mode
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (isStandalone) {
      return;
    }

    // Android / Desktop Chrome beforeinstallprompt handler
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);

    // Detect iOS Safari
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !/chrome|crios|fxios/.test(userAgent);
    if (isIosDevice && isSafari) {
      setIsIos(true);
      setIsInstallable(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIos) {
      setShowIosGuide(true);
      return;
    }

    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setIsInstallable(false);
    }
    setDeferredPrompt(null);
  };

  if (!isInstallable || dismissed) return null;

  return (
    <div className={`rounded-lg bg-emerald-950/70 border border-emerald-600/40 p-2.5 text-slate-200 ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded bg-emerald-600/30 text-emerald-400">
            <Smartphone className="h-4 w-4" />
          </div>
          <div>
            <p className="text-xs font-semibold text-white leading-tight">Install Mobile App</p>
            <p className="text-[10px] text-emerald-300/80">Add to home screen for site visits</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleInstallClick}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-emerald-600 hover:bg-emerald-500 text-white transition-all shadow-sm"
          >
            <Download className="h-3 w-3" />
            Install
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="p-1 text-slate-400 hover:text-slate-200 transition-colors"
            title="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {showIosGuide && (
        <div className="mt-2 pt-2 border-t border-emerald-800/40 text-[11px] text-emerald-200/90 leading-tight">
          To install on iPhone: tap the <strong>Share</strong> icon in Safari and select <strong>Add to Home Screen</strong>.
        </div>
      )}
    </div>
  );
}
